import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Alert } from "@mui/material";
import { useQueryParam } from "./utils/useQueryParam";
import { useCompanyQuote } from "./hooks/useCompanyQuote";
import { useCompanyProfile } from "./hooks/useCompanyProfile";
import { useAnalytics } from "../../../hooks/useAnalytics";
import ViewTicker from "./ViewTicker";
import { ViewTickerSkeleton } from "./ViewTickerSkeleton";
import ErrorDisplay from "../../common/ErrorDisplay";
import { StockCurrencyProvider } from "../../../contexts/StockCurrencyContext";

const ViewTickerLoader: React.FC = () => {
  const ticker = useQueryParam("ticker");

  // Guarantee the skeleton PAINTS before the heavy ViewTicker tree mounts.
  // With warm react-query caches isLoading is false on the very first render,
  // so navigating here went tap → multi-second synchronous mount with no
  // visual feedback (reads as a hang on phone CPUs). The double-RAF puts at
  // least one painted skeleton frame between tap and mount.
  // The dashboard scrolls; without this the ticker page inherits that scroll
  // offset and appears to open halfway down.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [ticker]);

  const [skeletonPainted, setSkeletonPainted] = useState(false);
  useEffect(() => {
    let inner: number | undefined;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setSkeletonPainted(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner !== undefined) cancelAnimationFrame(inner);
    };
  }, []);

  const {
    data: quoteData,
    error: quoteError,
    isLoading: quoteLoading,
  } = useCompanyQuote(ticker ?? "");

  // Fetch profile to get currency info for the StockCurrencyProvider
  const {
    data: profileData,
    error: profileError,
    isLoading: profileLoading,
  } = useCompanyProfile(ticker ?? "");
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;

  // Normalize the quote payload (handles array / nested / direct shapes).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawCompany = useMemo((): any => {
    if (Array.isArray(quoteData)) return quoteData[0];
    if (quoteData && typeof quoteData === "object") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nested = quoteData as any;
      if (nested.company?.data) return nested.company.data;
      if (nested.quoteData?.data) return nested.quoteData.data;
      if (nested.data) return nested.data;
      return quoteData;
    }
    return undefined;
  }, [quoteData]);

  // Value-stable company: the 10s quote poll returns a fresh object every
  // tick even when nothing moved (the timestamp field always changes).
  // Keying identity on the displayed values is what lets the memoized page
  // below skip ticks where no number actually changed.
  const company = useMemo(
    () => rawCompany,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      rawCompany?.symbol,
      rawCompany?.name,
      rawCompany?.exchange,
      rawCompany?.price,
      rawCompany?.change,
      rawCompany?.changePercentage,
      rawCompany?.volume,
      rawCompany?.dayLow,
      rawCompany?.dayHigh,
      rawCompany?.yearLow,
      rawCompany?.yearHigh,
      rawCompany?.marketCap,
      rawCompany?.open,
      rawCompany?.previousClose,
      rawCompany?.priceAvg50,
      rawCompany?.priceAvg200,
    ],
  );

  // Perf telemetry: how long a stock page takes from mount to real content.
  const { trackFeature } = useAnalytics();
  const mountedAtRef = useRef(performance.now());
  const loadReportedRef = useRef(false);
  const ready =
    !quoteLoading && !profileLoading && skeletonPainted && !!company?.symbol;
  useEffect(() => {
    if (!ready || loadReportedRef.current) return;
    loadReportedRef.current = true;
    trackFeature("perf_ticker_load", {
      ticker,
      duration_ms: Math.round(performance.now() - mountedAtRef.current),
    });
  }, [ready, ticker, trackFeature]);

  // Show skeleton while loading quote or profile data
  if (quoteLoading || profileLoading || !skeletonPainted) {
    return <ViewTickerSkeleton />;
  }

  // Show error state
  if (quoteError || profileError) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <ErrorDisplay
          error={quoteError ?? profileError}
          onRetry={() => window.location.reload()}
          variant="card"
          fullWidth
        />
      </Container>
    );
  }

  // Show error if no ticker provided
  if (!ticker) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="warning">
          No ticker symbol provided in the URL
        </Alert>
      </Container>
    );
  }

  // No usable payload at all (empty array / non-object response)
  if (!company) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="info">
          No company data found for ticker: {ticker}
        </Alert>
      </Container>
    );
  }

  // Additional validation for company data
  if (!company.symbol) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">
          Invalid company data received from API.
        </Alert>
      </Container>
    );
  }

  // Render the actual ViewTicker component with data
  // Wrap with StockCurrencyProvider so all child components can access currency formatting
  return (
    <StockCurrencyProvider currency={profile?.currency} exchange={profile?.exchange}>
      <ViewTicker company={company} ticker={ticker} />
    </StockCurrencyProvider>
  );
};

export default ViewTickerLoader;